export interface IHttpData {
  getFormData: () => Promise<{ cd_form: number, ds_caption: string }>
  getView: (body?: any, spinner?: boolean) => Promise<any>
  create: (body: any) => Promise<any>
  get: (id: number, fields?: string) => Promise<any>
  getAll: (body?: any) => Promise<any>
  getField: (id: number, field: string) => Promise<any>
  remove: (id: number) => Promise<any>
  update: (id: string | number, body: any) => Promise<any>
  getGrid: () => Promise<any>
  find: (payload: string, body?: object, spinner?: boolean) => Promise<any>
  findFirst: (payload: string, body?: object, spinner?: boolean) => Promise<any>
  execFirst: (payload: string, body?: object, spinner?: boolean) => Promise<any>
  findField: (payload: string, body?: object, field?: string, spinner?: boolean) => Promise<any>
  exec: (payload: string, body?: object, spinner?: boolean) => Promise<any>
  authGrid: () => Promise<any>
  getState: (body?: any) => Promise<any>
  getMenu: () => Promise<any>
  getLog: (id: number) => Promise<any>
  getList: (body?: any) => Promise<any>
  getItem: (id: number) => Promise<any>
  getId: (body?: any) => Promise<number | null>
  getTitle: () => Promise<string>
  getName: (id: number) => Promise<string>
  getBlob: (id: number, fieldname: string, filename: string, spinner?: boolean) => Promise<any>
  setBlob: (id: number, content: string, fieldname: string, spinner?: boolean) => Promise<boolean>
  getItemList: () => Promise<any[]>
  getItemFind: (payload: string, body?: any) => Promise<any[]>
  getFile: (payload: any, filename: string, cmd: string) => Promise<any>
}

export const useBaseStore = (url: string): IHttpData => {
  const getView = async (body?: any, spinner?: boolean) => {
    try {
      return await useHttp(`${url}/view`, { method: 'post', body, spinner })
    } catch {
      return []
    }
  }

  const create = async (body: any) => {
    return await useHttp(`${url}`, { method: 'post', body })
  }

  const get = async (id: number, fields: string = '') => {
    return await useHttp(`${url}/${id}?fields=${fields}`, { method: 'get' })
  }

  const getAll = async (body?: any) => {
    return await useHttp(`${url}`, { method: 'get', body })
  }

  const getField = async (id: number, field: string) => {
    const data = await get(id, field)
    if (!data)
      return data
    if (Object.keys(data).length > 1)
      return data
    else if (Object.keys(data).length === 1)
      return Object.values(data)[0]
    else
      return data
  }

  const remove = async (id: number) => {
    return await useHttp(`${url}/${id}`, { method: 'delete' })
  }

  const update = async (id: string | number, body: any) => {
    return await useHttp(`${url}/${id}`, { method: 'put', body })
  }

  const getGrid = async () => {
    return useGrid().getCols(await useHttp(`${url}/grid`))
  }

  const sendHttp = async (payload: any, method: any, body?: any, spinner?: boolean) => {
    return await useHttp(`${url}/${payload}`, { method, body, spinner })
  }

  const callPost = async (payload: string, body?: any, spinner?: boolean): Promise<any> => {
    return await sendHttp(payload, 'post', body, spinner)
  }

  const callGet = async (payload: string, body?: any, spinner?: boolean): Promise<any> => {
    return await sendHttp(payload, 'get', body, spinner)
  }

  const find = async (payload: string, body: object = {}, spinner?: boolean) => {
    return await callPost(`find/${payload}`, body, spinner)
  }

  const findFirst = async (payload: string, body: object = {}, spinner?: boolean) => {
    const response = await find(payload, body, spinner)
    return response?.length ? response[0] : null
  }

  const execFirst = async (payload: string, body: object = {}, spinner?: boolean) => {
    const response = await exec(payload, body, spinner)
    return response?.length ? response[0] : null
  }

  const findField = async (payload: string, body: object = {}, field?: string, spinner?: boolean) => {
    const response = await findFirst(payload, body, spinner)
    return response ? field ? response[field] : Object.values(response)[0] : null
  }

  const exec = async (payload: string, body: object = {}, spinner?: boolean) => {
    return await callPost('exec/' + payload, body, spinner)
  }

  const authGrid = async (): Promise<any> => {
    return await callPost('auth')
  }

  const getState = async (body: any) => {
    const response = await callPost('state', body)
    return response?.bb_grid ? JSON.parse(Decode64(response.bb_grid)) : []
  }

  const getFormData = async (): Promise<{ cd_form: number, ds_caption: string }> => {
    return await callGet('form')
  }

  const getMenu = async () => {
    return await callGet('menu')
  }

  const getLog = async (id: number) => {
    return await callGet(`log/${id}`)
  }

  const getList = async (body?: any) => {
    return await callGet('list', body)
  }

  const getItem = async (id: number) => {
    const response = await callGet(`list/${id}`)
    return response ? response[0] : null
  }

  const getId = async (body?: any): Promise<number | null> => {
    const response = await callPost('id', body)
    return response ? Number(Object.values(response)[0]) : null
  }

  const getName = async (id: number): Promise<string | null> => {
    const response = await callGet(`list/${id}`)
    const item = response?.length ? response[0] : null
    if (!item) return null
    return Object.keys(item).length > 1 ? String(Object.values(item)[1]) : null
  }

  const getTitle = async (): Promise<string | null> => {
    const response = await callGet('title')
    return response ? String(Object.values(response)[0]) : null
  }

  const getBlob = async (id: number, fieldname: string, filename: string, spinner?: boolean) => {
    return await useHttp(`${url}/blob/${id}?fieldname=${fieldname}&filename=${filename}`, { method: 'post', fileDownload: true, spinner })
  }

  const setBlob = async (id: number, content: string, fieldname: string, spinner?: boolean) => {
    return await useHttp(`${url}/${id}?fieldname=${fieldname}`, { method: 'put', body: content, spinner, fileDownload: true })
  }

  const getFile = async (payload: any, filename: string, cmd: string) => {
    return await useHttp(`${url}?filename=${filename}&cmd=${cmd}`, { method: 'post', body: payload, fileDownload: true })
  }

  const getItemList = async () => {
    return getFieldList(await getList())
  }

  const getItemFind = async (payload: string, body?: any) => {
    return getFieldList(await find(payload, body))
  }

  return {
    getFile,
    getFormData,
    getName,
    getItem,
    getItemList,
    getItemFind,
    remove,
    create,
    update,
    get,
    getField,
    getAll,
    getLog,
    getGrid,
    getState,
    getList,
    getView,
    getMenu,
    getTitle,
    getId,
    execFirst,
    findFirst,
    findField,
    authGrid,
    find,
    exec,
    setBlob,
    getBlob
  }
}
